import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatTableExporterDirective } from 'mat-table-exporter';
import { DashboardService } from '../service/dashboard.service';
import Swal from 'sweetalert2';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { UpdateBillingStatusComponent } from '../../dialogs/update-billing-status/update-billing-status.component';

export interface BillingRecord {
  invoiceNumber: string;
  description: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  agentCount: number;
  amountDue: number;
  invoiceDate: string;
  status: string;
  paymentDate: string;
}

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss']
})
export class BillingComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTableExporterDirective) exporter: MatTableExporterDirective;

  billingForm: FormGroup;
  isLoading = false;
  hasSearched = false;
  searchMode: 'month' | 'dateRange' = 'month';

  displayedColumns: string[] = [
    'invoiceNumber',
    'description',
    'billingPeriodStart',
    'billingPeriodEnd',
    'agentCount',
    'amountDue',
    'invoiceDate'
  ];

  dataSource = new MatTableDataSource<BillingRecord>([]);

  totalPremium = 0;
  totalTax = 0;
  totalAmount = 0;
  paidCount = 0;
  unpaidCount = 0;

  branchesData: any[] = [];
  selectedBranch: string = '';

  statusOptions = ['ALL', 'PAID', 'UNPAID', 'OVERDUE', 'PENDING'];
  paymentModes = ['ALL', 'CASH', 'CHEQUE', 'MOBILE MONEY', 'BANK TRANSFER'];
  months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  constructor(
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    const currentMonth = this.months[new Date().getMonth()];
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    this.billingForm = this.fb.group({
      month:       [currentMonth, Validators.required],
      startDate:   [firstDay],
      endDate:     [today],
      search_text: ['']
    });
  }

  setSearchMode(mode: 'month' | 'dateRange') {
    this.searchMode = mode;
    this.dataSource.data = [];
    this.hasSearched = false;
    this.totalPremium = 0;
    this.totalTax = 0;
    this.totalAmount = 0;
    this.paidCount = 0;
    this.unpaidCount = 0;

    if (mode === 'month') {
      this.billingForm.get('month').setValidators(Validators.required);
      this.billingForm.get('startDate').clearValidators();
      this.billingForm.get('endDate').clearValidators();
    } else {
      this.billingForm.get('month').clearValidators();
      this.billingForm.get('startDate').setValidators(Validators.required);
      this.billingForm.get('endDate').setValidators(Validators.required);
    }
    this.billingForm.get('month').updateValueAndValidity();
    this.billingForm.get('startDate').updateValueAndValidity();
    this.billingForm.get('endDate').updateValueAndValidity();
  }

  loadBranches() {
    this.dashboardService.getBranchData().subscribe(
      (res: any) => {
        if (res && res.profile) {
          this.branchesData = res.profile;
        }
      },
      (err) => {
        console.error('Could not load branches', err);
      }
    );
  }

  get resultLabel(): string {
    if (this.searchMode === 'month') {
      const m: string = this.billingForm.get('month').value || '';
      return 'Invoice \u2014 ' + m.charAt(0) + m.slice(1).toLowerCase();
    }
    const from = this.datePipe.transform(this.billingForm.get('startDate').value, 'dd/MM/yyyy');
    const to   = this.datePipe.transform(this.billingForm.get('endDate').value,   'dd/MM/yyyy');
    return 'Invoice \u2014 ' + from + ' to ' + to;
  }

  fetchBillingData() {
    if (this.billingForm.invalid) {
      this.billingForm.markAsTouched();
      return;
    }

    this.isLoading = true;
    this.hasSearched = false;

    const formVal = this.billingForm.value;

    if (this.searchMode === 'month') {
      this.dashboardService.getBillingData({ month: formVal.month }).subscribe(
        (res: any) => this.handleResponse(res, formVal.search_text),
        (err)      => this.handleError(err)
      );
    } else {
      const startDate = this.datePipe.transform(formVal.startDate, 'yyyy-MM-dd');
      const endDate   = this.datePipe.transform(formVal.endDate,   'yyyy-MM-dd');
      this.dashboardService.getBillingByDateRange(startDate, endDate).subscribe(
        (res: any) => this.handleResponse(res, formVal.search_text),
        (err)      => this.handleError(err)
      );
    }
  }

  private handleResponse(res: any, searchText: string) {
    this.isLoading = false;
    this.hasSearched = true;

    let records: BillingRecord[] = [];
    if (res && res.profile) {
      // wrapped response { profile: [...] }
      records = Array.isArray(res.profile) ? res.profile : [res.profile];
    } else if (Array.isArray(res)) {
      // direct array response
      records = res;
    } else if (res && res.invoiceNumber) {
      // single object response
      records = [res];
    }

    this.applySearchFilter(records, searchText);
    this.computeSummary(records);
  }

  private handleError(err: any) {
    this.isLoading = false;
    this.hasSearched = true;
    console.error('Billing data error', err);
    Swal.fire('Error', 'Could not load billing data. Please try again.', 'error');
  }

  applySearchFilter(records: BillingRecord[], text: string) {
    this.dataSource.data = records;
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });

    if (text && text.trim()) {
      this.dataSource.filter = text.trim().toLowerCase();
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  computeSummary(records: BillingRecord[]) {
    this.totalAmount = records.reduce((sum, r) => sum + (r.amountDue || 0), 0);
    this.paidCount   = records.filter(r => (r.status || '').toUpperCase() === 'PAID').length;
    this.unpaidCount = records.filter(r => (r.status || '').toUpperCase() !== 'PAID').length;
    // keep legacy totals unused but non-breaking
    this.totalPremium = 0;
    this.totalTax = 0;
  }

  resetForm() {
    this.initForm();
    this.dataSource.data = [];
    this.hasSearched = false;
    this.totalPremium = 0;
    this.totalTax = 0;
    this.totalAmount = 0;
    this.paidCount = 0;
    this.unpaidCount = 0;
  }

  getStatusClass(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'PAID': return 'status-paid';
      case 'UNPAID': return 'status-unpaid';
      case 'OVERDUE': return 'status-overdue';
      case 'PENDING': return 'status-pending';
      default: return '';
    }
  }

  exportToExcel() {
    if (this.exporter) {
      this.exporter.exportTable('xlsx', { fileName: this.resultLabel });
    }
  }

  openStatusDialog(row: BillingRecord) {
    const dialogRef = this.dialog.open(UpdateBillingStatusComponent, {
      width: '440px',
      data: { ...row }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'updated') {
        const index = this.dataSource.data.findIndex(r => r.invoiceNumber === row.invoiceNumber);
        if (index !== -1) {
          this.dataSource.data[index].status = result.payment_status;
          this.dataSource.data = this.dataSource.data.slice();
          this.computeSummary(this.dataSource.data);
        }
      }
    });
  }
}
